import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { PopupMenu, type PopupMenuItem } from '@kerfjs/ui/popup-menu';
import { type SafeHtml } from 'kerfjs';
import { Award, CircleX, Clock3, Gavel, Handshake, MessageCircleQuestionMark, RefreshCw, Ship } from 'lucide';

import { menuProps, type MenuTrigger } from '../lib/action-menu.js';
import { DECISION_LABELS } from './decision-badge.js';

const DECISION_ICONS = {
  approve: Award,
  negotiate: Handshake,
  reject: CircleX,
  defer: Clock3,
  import: Ship,
  're-quote': RefreshCw,
} as const;

interface DecisionSubmenuOptions {
  actions: readonly string[];
  actionName: string;
  lineId?: string;
  currentAction?: string | null;
  disabledActions?: ReadonlySet<string>;
  /** Adds Needs Clarification… after the decisions for a single line (HS2-R5H22P). */
  clarifyAction?: string;
}

/** The Decide entry with its checkbox submenu, shared by row and selection menus (Kerf PopupMenu
 *  nested choices, HS2-0ZYSMS). */
export function decisionSubmenuEntry({
  actions,
  actionName,
  lineId,
  currentAction,
  disabledActions = new Set(),
  clarifyAction,
}: DecisionSubmenuOptions): PopupMenuItem {
  const clarify: PopupMenuItem[] =
    clarifyAction === undefined
      ? []
      : [
          {
            label: 'Needs Clarification…',
            action: clarifyAction,
            icon: <LucideIcon icon={MessageCircleQuestionMark} name="" />,
            attributes: { 'data-item-id': lineId },
          },
        ];
  return {
    label: 'Decide',
    icon: <LucideIcon icon={Gavel} name="" />,
    submenu: [
      ...actions.map((action) => {
        const disabled = disabledActions.has(action);
        return {
          label: DECISION_LABELS[action] ?? action,
          action: actionName,
          icon: <LucideIcon icon={DECISION_ICONS[action as keyof typeof DECISION_ICONS]} name="" />,
          checked: currentAction === action,
          disabled,
          disabledReason: disabled
            ? 'Every selected line needs a recommended vendor and price for this action.'
            : undefined,
          attributes: { 'data-item-id': lineId, 'data-decide-action': action },
        } satisfies PopupMenuItem;
      }),
      ...clarify,
    ],
  };
}

/** A menu whose only command is the Decide submenu. */
export function DecisionSubmenu({ trigger, ...options }: DecisionSubmenuOptions & { trigger: MenuTrigger }): SafeHtml {
  return <PopupMenu {...menuProps(trigger, [decisionSubmenuEntry(options)])} />;
}
